import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Paper,
  Alert,
  Button,
  CircularProgress
} from '@mui/material';
import PropTypes from 'prop-types';

import { ROUTES } from '../../../../constants/routes';
import { rolesID } from '../../../../constants/rolesID';
import { APPLICATION_STATUS, DISABLED_CONTROL_TOOLTIP, PERMISSIONS } from '../../../../constants/variables';
import { DoubleField } from '../../components/DoubleField';
import { CustomButton } from '../../../../components/CustomButton';
import { CustomInput } from '../../../../components/CustomInput';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  getDetailedApplication,
  postHandlerApplication,
  getSearchCustomer
} from '../../../../store/support/actions';
import { formatDate } from '../../../../utils/formatDate';
import { maskNumberPhone } from '../../../../utils/maskNumberPhone';
import { timeIsOver } from '../../../../utils/timeIsOver';
import { getColorStatusApplication } from '../../../../utils/getColorStatusApplication';
import { useAnimation } from '../../../../hooks/useAnimation';
import { useSearchCustomerBy } from '../../../../hooks/useSearchCustomerBy';
import { hiddenByRole } from '../../../../utils/hiddenByRole';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';

export const DetailApplication = () => {
  const { userData: info } = useSelector((state) => state.currentUser);
  const { application, customer, applicationFetching, fetching } = useSelector((state) => state.support);
  const [startAnimation, validateAnimationStyles] = useAnimation();
  const [isEdit, setIsEdit] = useState(false);
  const [button, setButton] = useState();
  const { createPath, getParams } = useSearchCustomerBy();
  const { userPermissions, currentUsersPermissions } = useSelector((state) => state.userPermissions);
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);

  const [numberOfBonuses, setNumberOfBonuses] = useState(application?.amount);

  const dispatch = useDispatch();
  const params = useRouteMatch();

  const submitApplication = useCallback(
    (status) => {
      if (!isEdit) {
        dispatch(
          postHandlerApplication(params.params.appID, {
            request_status: status,
            amount: application?.amount === numberOfBonuses ? null : numberOfBonuses
          })
        );
      } else startAnimation();
    },  // eslint-disable-next-line
    [numberOfBonuses, isEdit]
  );

  const isDisabledUpdateBonusRequestsPermission = !visibleBySectionPermission(permissions, PERMISSIONS.SUPPORT.UpdateSupportBonusRequests.value);

  useEffect(() => {
    setNumberOfBonuses(application?.amount);
  }, [application]);

  useEffect(() => {
    if (!Array.isArray(customer) || params.params?.id) {
      dispatch(getSearchCustomer(getParams()));
    }
    dispatch(getDetailedApplication(params.params.appID));
  }, // eslint-disable-next-line
    []);

  const checkLocation = params.path.includes('application')
    ? { label: 'Мои заявки', path: ROUTES.APPLICATIONS }
    : { label: 'Техподдержка', path: ROUTES.SUPPORT };

  if (applicationFetching) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
        <CircularProgress data-testid="CircularProgress"/>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs mb="24px" aria-label="breadcrumb">
        <Link component={RouterLink} underline="hover" color="inherit" to={checkLocation.path} data-testid="BreadcrumbsLink">
          {checkLocation.label}
        </Link>

        {!params.path.includes('application') ? (
          <Link
            component={RouterLink}
            underline="hover"
            color="inherit"
            to={createPath(ROUTES.SUPPORT_USER_CARD)}
            data-testid="CustomerNameLink"
          >
            {customer?.name}
          </Link>
        ) : null}

        <Typography>{application?.request_num}</Typography>
      </Breadcrumbs>

      <Paper
        sx={{
          padding: '24px',
          marginBottom: '16px'
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px'
          }}>
          <Box display="flex" flexDirection="column" flex="1 1 30%">
            <Box sx={{ marginBottom: '16px' }}>
              <DoubleField title="Заявку создал" description={application?.manager?.fullname} />
            </Box>

            <Box sx={{ marginBottom: '16px' }}>
              <DoubleField
                title="Дата создания"
                description={formatDate(application?.created_at)}
              />
            </Box>

            <Box sx={{ marginBottom: '16px' }}>
              <DoubleField title="SLA" description={formatDate(application?.sla)} />
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            flex="1 1 70%"
            border="1px solid transparent"
            borderLeftColor="action.disabled"
            minHeight="180px"
            paddingLeft="24px">
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography
                variant="h5"
                sx={{
                  fontSize: '26px',
                  fontWeight: 500,
                  color: timeIsOver(application?.sla) && 'error.main'
                }}>
                {application?.request_num}
              </Typography>

              <Typography sx={{ color: getColorStatusApplication(application?.request_status) }}>
                {application?.request_status}
              </Typography>
            </Box>
            <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" mb={3}>
              <DoubleField title="ФИО клиента" description={application?.subscriber?.name} />

              <DoubleField
                title="Номер телефона"
                description={maskNumberPhone(application?.subscriber?.mobile_phone)}
              />
              <DoubleField title="Бонусный счет" description={12312313452} />

              <DoubleField title="Статус клиента" description={application?.subscriber_status} />
            </Box>
            <Box>
              {APPLICATION_STATUS.new.label === application?.request_status &&
                hiddenByRole(info?.role?.id, [
                  rolesID.ROLE_SUPERVISER_TP,
                  rolesID.ROLE_ADMINISTRATOR
                ]) && (
                  <CustomButton
                    onClick={() => submitApplication(APPLICATION_STATUS.in_processing.status)}
                    variant="outlined"
                    size="small"
                    disabled={fetching || isDisabledUpdateBonusRequestsPermission}
                    sx={{ marginRight: '16px', width: '120px', height: '42px' }}
                    data-testid="SubmitButton"
                    tooltip={isDisabledUpdateBonusRequestsPermission ? DISABLED_CONTROL_TOOLTIP : null}
                  >
                    {fetching ? <CircularProgress size={18} data-testid="CircularProgress2"/> : 'В работу'}
                  </CustomButton>
                )}

              {APPLICATION_STATUS.in_processing.label === application?.request_status &&
                hiddenByRole(info?.role?.id, [
                  rolesID.ROLE_SUPERVISER_TP,
                  rolesID.ROLE_ADMINISTRATOR
                ]) && (
                  <>
                    <CustomButton
                      onClick={(e) => {
                        setButton(e.target.outerText);
                        submitApplication(APPLICATION_STATUS.executed.status);
                      }}
                      variant="outlined"
                      size="small"
                      sx={{ marginRight: '16px', width: '130px', height: '42px' }}
                      disabled={button === 'Начислить' && fetching || isDisabledUpdateBonusRequestsPermission}
                      tooltip={isDisabledUpdateBonusRequestsPermission ? DISABLED_CONTROL_TOOLTIP : null}
                      data-testid="AccrueButton"
                    >
                      {fetching && button === 'Начислить' ? (
                        <CircularProgress size={18} />
                      ) : (
                        'Начислить'
                      )}
                    </CustomButton>
                    <CustomButton
                      onClick={(e) => {
                        setButton(e.target.outerText);
                        submitApplication(APPLICATION_STATUS.rejected.status);
                      }}
                      color="error"
                      size="small"
                      variant="outlined"
                      sx={{ width: '130px', height: '42px' }}
                      disabled={button === 'Отклонить' && fetching || isDisabledUpdateBonusRequestsPermission}
                      tooltip={isDisabledUpdateBonusRequestsPermission ? DISABLED_CONTROL_TOOLTIP : null}
                      data-testid="RejectButton"
                    >
                      {fetching && button === 'Отклонить' ? (
                        <CircularProgress size={18} />
                      ) : (
                        'Отклонить'
                      )}
                    </CustomButton>
                  </>
                )}
            </Box>
          </Box>
        </Box>
      </Paper>
      <Paper
        sx={{
          padding: '24px 32px',
          wordWrap: 'normal',
          wordBreak: 'break-all'
        }}>
        <DoubleField
          title="Причина начисления"
          description={application?.reason_for_charge?.name}
          display="flex"
          mb={3}
        />

        <DoubleField
          title="Описание"
          display="flex"
          mb={3}
          description={application?.description}
        />
        {!isEdit ? (
          <DoubleField
            mb={3}
            title="Количество бонусов"
            display="flex"
            description={numberOfBonuses}
          />
        ) : (
          <Box display="flex" alignItems="center">
            <Typography sx={{ color: 'text.secondary', minWidth: 180 }}>
              Количество бонусов
            </Typography>
            <CustomInput
              type="number"
              sx={{ display: 'flex', width: '240px', p: 0.5, pr: 0 }}
              size="small"
              value={numberOfBonuses}
              onChange={(e) => setNumberOfBonuses(+e.target.value)}
            />
            <Box ml={4} display="flex" alignItems="center">
              <CheckIcon
                sx={{
                  marginRight: '16px',
                  cursor: 'pointer',
                  ...validateAnimationStyles
                }}
                color="success"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEdit(false);
                }}
                data-testid="CheckIcon"
              />
              <CloseIcon
                sx={{
                  cursor: 'pointer',
                  ...validateAnimationStyles
                }}
                color="error"
                onClick={(e) => {
                  setNumberOfBonuses(application?.amount);
                  e.stopPropagation();
                  setIsEdit(false);
                }}
                data-testid="CloseIcon"
              />
            </Box>
          </Box>
        )}
        {!isEdit &&
          APPLICATION_STATUS.in_processing.label === application?.request_status &&
          hiddenByRole(info?.role?.id, [
            rolesID.ROLE_SUPERVISER_TP,
            rolesID.ROLE_ADMINISTRATOR
          ]) && (
            <Alert
              severity="warning"
              sx={{
                color: '#151C22',
                background: '#F5E6C7',
                fontSize: '16px',
                alignItems: 'center'
              }}
              closeText="Изменить"
              onClose={() => {}}
              action={
                <Button
                  type="text"
                  color="primary"
                  size="small"
                  onClick={() => setIsEdit(true)}
                  sx={{
                    textTransform: 'capitalize',
                    fontSize: '20px',
                    lineHeight: '24px',
                    padding: 0,
                    marginRight: '16px'
                  }}>
                  Изменить
                </Button>
              }>
              При нажатии “Начислить”, пользователю будет переведено{' '}
              <b>{numberOfBonuses} бонусов</b>
            </Alert>
          )}
      </Paper>
    </Box>
  );
};

DetailApplication.propTypes = {
  numberApplication: PropTypes.string,
  nameCreator: PropTypes.string,
  dateCreate: PropTypes.string,
  SLA: PropTypes.string,
  bonusesNumber: PropTypes.string
};
